/**
 * @file Fetches the organization logo as a raster buffer with its pixel size
 * for embedding in XLSX exports. Consumed by `useXlsxAttachment` and
 * `useFormXlsxExport`. With no `logoUrl`, or a remote one that local dev skips,
 * the bundled Syncretic logo stands in. Any fetch or decode failure returns
 * `undefined` and the workbook carries no logo rather than failing the export.
 */
import { deploymentEnvironment, environmentType } from '@/config';

type XlsxLogoImage = {
	buffer: ArrayBuffer;
	extension: 'png' | 'jpeg';
	widthPx: number;
	heightPx: number;
};

const rasterPixelSizeFromBuffer = (imageSize: typeof import('image-size').default, buffer: ArrayBuffer): { width: number; height: number } | undefined => {
	try {
		const result = imageSize(new Uint8Array(buffer));
		const { width, height } = result;
		if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
			return { width, height };
		}
	} catch {
		/* invalid or unsupported raster */
		return undefined;
	}
};

const LOCAL_FALLBACK = '/art/Syncretic-logo-horizontal@3x.png';

const isRemoteHttpUrl =(url: string): boolean => {
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch {
		return false;
	}
};

const isLocalhostUrl = (url: string): boolean => {
	try {
		const parsed = new URL(url);
		return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
	} catch {
		return false;
	}
};

const extensionFromContentType = (contentType: string): 'png' | 'jpeg' => {
	if (contentType.includes('jpeg') || contentType.includes('jpg')) return 'jpeg';
	return 'png';
};

async function fetchXlsxLogoImage(logoUrl: string | undefined): Promise<XlsxLogoImage | undefined> {
	const isLocalDev = environmentType === 'development' && deploymentEnvironment === 'local';

	const resolvedUrl =
		!logoUrl || (isLocalDev && isRemoteHttpUrl(logoUrl) && !isLocalhostUrl(logoUrl)) ? LOCAL_FALLBACK : logoUrl;

	try {
		const { default: imageSize } = await import('image-size');
		const response = await fetch(resolvedUrl);
		if (!response.ok) return undefined;
		const blob = await response.blob();
		const buffer = await blob.arrayBuffer();
		const extension = extensionFromContentType(blob.type);
		const pixelSize = rasterPixelSizeFromBuffer(imageSize, buffer);
		if (pixelSize === undefined) return undefined;
		return { buffer, extension, widthPx: pixelSize.width, heightPx: pixelSize.height };
	} catch {
		return undefined;
	}
}

export default fetchXlsxLogoImage;
export type { XlsxLogoImage };
